import React, { useState } from 'react';

import { CustomOperatorValue } from '@aline/common';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import type { GridSortModel } from '@mui/x-data-grid-premium';
import { GridLogicOperator, useGridApiContext } from '@mui/x-data-grid-premium';
import { Clock, File, Signature, Star, UserRound } from 'lucide-react';
import { DropdownSelectButton } from 'src/components';
import { Apollo } from 'src/gql/generated';
import { useSelectCurrentUser } from 'src/gql/hooks/user';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

export type RepositoryView =
  | 'recently-created'
  | 'recently-accessed'
  | 'favorites'
  | 'myDocuments'
  | 'signedContracts';

interface ViewOption {
  icon: JSX.Element;
  label: string;
  value: RepositoryView;
}

const VIEW_OPTIONS: ViewOption[] = [
  { icon: <Star size={16} />, label: 'Favorites', value: 'favorites' },
  { icon: <UserRound size={16} />, label: 'My documents', value: 'myDocuments' },
  { icon: <Signature size={16} />, label: 'Signed contracts', value: 'signedContracts' },
  { icon: <File size={16} />, label: 'Recently created', value: 'recently-created' },
  { icon: <Clock size={16} />, label: 'Recently accessed', value: 'recently-accessed' },
];

const RECENTLY_CREATED_SORT_MODEL: GridSortModel = [{ field: 'createdAt', sort: 'desc' }];

const RECENTLY_ACCESSED_SORT_MODEL: GridSortModel = [
  { field: 'lastAccessedByRequesterAt', sort: 'desc' },
];

const useStyles = makeStyles()(theme => ({
  menuIcon: {
    alignItems: 'center',
    color: theme.appColors.iconsType,
    display: 'flex',
    minWidth: '0 !important',
    paddingRight: theme.spacing(1),
  },
}));

interface RepositoryViewsDropdownProps {
  activeView: RepositoryView;
  onViewChange: (view: RepositoryView) => void;
}

export function RepositoryViewsDropdown({
  activeView,
  onViewChange,
}: RepositoryViewsDropdownProps): JSX.Element {
  const { classes } = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const activeOption = VIEW_OPTIONS.find(opt => opt.value === activeView) ?? VIEW_OPTIONS[0];

  return (
    <>
      <DropdownSelectButton
        ref={setAnchorEl}
        icon={activeOption.icon}
        label={activeOption.label}
        selected={menuOpen}
        variant="outlined"
        onClick={() => setMenuOpen(!menuOpen)}
      />
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        open={menuOpen}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        onClose={() => setMenuOpen(false)}
      >
        {VIEW_OPTIONS.map(opt => (
          <MenuItem
            key={opt.value}
            selected={opt.value === activeView}
            onClick={() => {
              setMenuOpen(false);
              onViewChange(opt.value);
            }}
          >
            <ListItemIcon className={classes.menuIcon}>{opt.icon}</ListItemIcon>
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/**
 * Apply the view's filter model to the DataGrid. Handles the four non-navigation
 * views (recent/favorites/myDocuments/signedContracts). Trash is handled
 * separately via navigation in the parent.
 */
export function useApplyRepositoryView() {
  const apiRef = useGridApiContext();
  const currentUser = useSelectCurrentUser();

  return React.useCallback(
    (view: RepositoryView) => {
      if (view === 'recently-created') {
        apiRef.current.setFilterModel({ items: [] });
        apiRef.current.setSortModel(RECENTLY_CREATED_SORT_MODEL);
        return;
      }

      if (view === 'recently-accessed') {
        apiRef.current.setFilterModel({ items: [] });
        apiRef.current.setSortModel(RECENTLY_ACCESSED_SORT_MODEL);
        return;
      }

      if (view === 'favorites') {
        apiRef.current.setFilterModel({
          items: [
            {
              field: Apollo.ReportColumnFileField.RequesterFavorited,
              id: uuid4(),
              operator: 'is',
              value: 'true',
            },
          ],
          logicOperator: GridLogicOperator.And,
        });
        return;
      }

      if (view === 'myDocuments') {
        if (!currentUser) return;
        apiRef.current.setFilterModel({
          items: [
            {
              field: Apollo.ReportColumnFileField.Owner,
              id: uuid4(),
              operator: CustomOperatorValue.Owner,
              value: [
                {
                  __typename: 'User',
                  avatarColor: currentUser.avatarColor,
                  displayName: currentUser.displayName,
                  id: currentUser.id,
                },
              ],
            },
          ],
          logicOperator: GridLogicOperator.And,
        });
        return;
      }

      if (view === 'signedContracts') {
        apiRef.current.setFilterModel({
          items: [
            {
              field: 'status',
              id: uuid4(),
              operator: CustomOperatorValue.DocumentStatus,
              value: [Apollo.DocumentStatus.Signed],
            },
          ],
          logicOperator: GridLogicOperator.And,
        });
        return;
      }
    },
    [apiRef, currentUser]
  );
}
